/**
 * Панель ручного подбора канона для термина культуры хозяйства.
 *
 * Открывается по «Изменить» / «Найти вручную». Поиск по системному справочнику
 * `reference.crops` (по названию + народным синонимам — на стороне бэка), выбор
 * канона + необязательное НАЗНАЧЕНИЕ ПОСЕВА → change-match. Разложение «культура
 * + назначение» делается один раз здесь: агроном при посеве продолжает выбирать
 * своё привычное название («Озимая пшеница на зелёный корм»).
 *
 * Альтернативные исходы: «это не культура» (подтип уточняется в NotACropDialog)
 * и «нужной культуры нет в справочнике» → escalate(reason='new_crop').
 */
import { useEffect, useState } from 'react';
import { Search, Check, Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { cropLabel } from '@/lib/cropLabel';
import { toast } from '@/lib/toast';
import { ApiError } from '@/lib/api';
import { useGrowingGoals } from '@/components/fields/card/api';
import {
  useCanonSearch,
  useChangeMatch,
  useConfirmMatch,
  useEscalate,
  type CropQueueItem,
} from '@/lib/reference/tenantCrops';

/** Radix Select не умеет пустое значение — сентинел «назначение не задано». */
const NO_GOAL = '__none__';

function errMsg(e: unknown, fallback: string): string {
  return e instanceof ApiError ? e.detail : fallback;
}

export function CropMatchDialog({
  item,
  open,
  onOpenChange,
  onRequestNotACrop,
}: {
  item: CropQueueItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Передать термин в диалог уточнения подтипа «не культура». */
  onRequestNotACrop?: (item: CropQueueItem) => void;
}) {
  const [query, setQuery] = useState('');
  const [goalId, setGoalId] = useState<string>(NO_GOAL);
  const [askNewCrop, setAskNewCrop] = useState(false);
  const [note, setNote] = useState('');

  const search = useCanonSearch(query);
  const goals = useGrowingGoals();
  const changeMatch = useChangeMatch();
  const confirm = useConfirmMatch();
  const escalate = useEscalate();

  const busy = changeMatch.isPending || confirm.isPending || escalate.isPending;

  // Подставляем сохранённое назначение при открытии / смене термина.
  useEffect(() => {
    if (!open) return;
    setGoalId(item?.growing_goal_id ?? NO_GOAL);
    setAskNewCrop(false);
    setNote('');
  }, [open, item]);

  /** `null` = снять назначение; строка = задать. */
  const goalPayload: string | null = goalId === NO_GOAL ? null : goalId;
  const goalChanged = goalPayload !== (item?.growing_goal_id ?? null);

  function close() {
    setQuery('');
    onOpenChange(false);
  }

  function handlePick(cropId: string) {
    if (!item) return;
    changeMatch.mutate(
      { itemId: item.id, cropId, growingGoalId: goalPayload },
      {
        onSuccess: () => {
          toast.success('Культура сопоставлена');
          close();
        },
        onError: (e) =>
          toast.error(errMsg(e, 'Не удалось сопоставить культуру')),
      },
    );
  }

  function handleSaveGoal() {
    if (!item) return;
    confirm.mutate(
      { itemId: item.id, growingGoalId: goalPayload },
      {
        onSuccess: () => {
          toast.success('Назначение сохранено');
          close();
        },
        onError: (e) => toast.error(errMsg(e, 'Не удалось сохранить назначение')),
      },
    );
  }

  function handleNewCrop() {
    if (!item) return;
    escalate.mutate(
      { itemId: item.id, reason: 'new_crop', note },
      {
        onSuccess: () => {
          toast.info('Отправлено на добавление в справочник');
          close();
        },
        onError: (e) => toast.error(errMsg(e, 'Не удалось отправить')),
      },
    );
  }

  const results = search.data ?? [];
  const goalOptions = goals.data ?? [];

  return (
    <Dialog open={open} onOpenChange={(o) => (o ? onOpenChange(o) : close())}>
      <DialogContent className="max-h-[85svh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Сопоставить «{item?.local_name ?? '—'}»</DialogTitle>
          <DialogDescription>
            Найдите культуру в системном справочнике. Поиск идёт и по народным
            названиям — напр. «маис» найдёт «Кукуруза».
          </DialogDescription>
        </DialogHeader>

        {/* Поиск канона */}
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Введите название культуры…"
            className="pl-9"
          />
        </div>

        {/* Результаты */}
        <div className="max-h-64 overflow-y-auto rounded-lg border border-border">
          {query.trim().length < 2 ? (
            <p className="p-4 text-sm text-muted-foreground">
              Начните вводить название — минимум 2 символа.
            </p>
          ) : search.isLoading ? (
            <div className="space-y-2 p-3">
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-full" />
              <Skeleton className="h-9 w-3/4" />
            </div>
          ) : search.isError ? (
            <p className="p-4 text-sm text-destructive">
              Не удалось загрузить справочник.
            </p>
          ) : results.length === 0 ? (
            <p className="p-4 text-sm text-muted-foreground">
              Ничего не найдено. Попробуйте другое название или отправьте термин
              на добавление в справочник.
            </p>
          ) : (
            <ul>
              {results.map((c) => {
                const selected = item?.ref_id === c.id;
                return (
                  <li
                    key={c.id}
                    className="flex items-center justify-between gap-3 border-b border-border px-3 py-2 last:border-b-0"
                  >
                    <div className="min-w-0">
                      <span className="font-medium text-foreground">
                        {cropLabel(c)}
                      </span>
                      {(c.latin_name || c.category) && (
                        <span className="ml-2 truncate text-xs text-muted-foreground">
                          {[c.latin_name, c.category]
                            .filter(Boolean)
                            .join(' · ')}
                        </span>
                      )}
                    </div>
                    <Button
                      size="sm"
                      variant={selected ? 'secondary' : 'outline'}
                      disabled={busy}
                      onClick={() => handlePick(c.id)}
                    >
                      {selected ? (
                        <>
                          <Check className="mr-1 h-3.5 w-3.5" /> Текущий
                        </>
                      ) : (
                        'Выбрать'
                      )}
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Назначение посева */}
        <div className="space-y-1.5">
          <Label htmlFor="crop-goal">Назначение посева · необязательно</Label>
          <Select
            value={goalId}
            onValueChange={setGoalId}
            disabled={busy || goals.isLoading}
          >
            <SelectTrigger id="crop-goal">
              <SelectValue placeholder="Не указывать" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_GOAL}>Не указывать</SelectItem>
              {goalOptions.map((g) => (
                <SelectItem key={g.id} value={g.id}>
                  {g.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Если ваше название содержит цель посева («на зелёный корм», «на
            семена») — укажите её здесь. Разложить достаточно один раз: при
            посеве вы продолжите выбирать своё привычное название.
          </p>
          {item?.ref_id && (
            <Button
              variant="outline"
              size="sm"
              disabled={busy || !goalChanged}
              onClick={handleSaveGoal}
            >
              {confirm.isPending && (
                <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
              )}
              Сохранить назначение
            </Button>
          )}
        </div>

        <Separator />

        {/* Альтернативные исходы */}
        <div className="space-y-2">
          <p className="text-xs text-muted-foreground">Не то? Другой исход:</p>
          <div className="flex flex-wrap gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => {
                if (!item) return;
                onOpenChange(false);
                onRequestNotACrop?.(item);
              }}
            >
              Это не культура (пар / угодье / смесь)
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={busy}
              onClick={() => setAskNewCrop((v) => !v)}
              aria-pressed={askNewCrop}
            >
              Это другая культура — её нет в справочнике
            </Button>
          </div>

          {askNewCrop && (
            <div className="space-y-2 rounded-lg border border-border p-3">
              <p className="text-xs text-muted-foreground">
                Нужной культуры нет в справочнике — отправим её на добавление.
                Например, пшеница твёрдая (Triticum durum) — это не мягкая
                (T. aestivum), сопоставлять их нельзя.
              </p>
              <Label htmlFor="new-crop-note">
                Что это за культура · необязательно
              </Label>
              <textarea
                id="new-crop-note"
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Напр.: пшеница твёрдая яровая, Triticum durum"
                className="flex w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              />
              <Button
                size="sm"
                disabled={busy}
                onClick={handleNewCrop}
                className={cn(escalate.isPending && 'opacity-70')}
              >
                {escalate.isPending && (
                  <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
                )}
                Отправить на добавление
              </Button>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
